import React, { useState, useEffect } from 'react';
import { 
  UserPlus, 
  Search, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Eye,
  Send,
  Users,
  Calendar,
  MapPin,
  Gift,
  ArrowLeft
} from 'lucide-react';
import { RewardedAdTrigger } from './AdProvider';
import RewardedAd from './RewardedAd';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JoinRequestsPage = ({ user, onBack }) => {
  const [activeTab, setActiveTab] = useState('my-requests'); // 'my-requests' or 'available-groups'
  const [myRequests, setMyRequests] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [joinRequest, setJoinRequest] = useState({ message: '' });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '', visible: false });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchMyRequests();
    fetchAvailableGroups();
  }, []);

  const showFeedback = (type, message) => {
    setFeedback({ type, message, visible: true });
    setTimeout(() => {
      setFeedback(prev => ({ ...prev, visible: false }));
    }, 5000);
  };

  const fetchMyRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/${user.id}/join-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMyRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching join requests:', error);
    }
  };

  const fetchAvailableGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/groups/search`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Pour l'API search, nous prenons tous les groupes disponibles
        const groups = data.groups || [];
        setAvailableGroups(groups);
      } else {
        console.error('Error fetching groups:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching available groups:', error);
    }
  };

  const handleJoinRequest = async (groupId) => {
    console.log('[JOIN_REQUEST] Starting join request for group:', groupId);
    
    if (!joinRequest.message.trim()) {
      showFeedback('error', 'Veuillez ajouter un message de motivation');
      console.log('[JOIN_REQUEST] Validation failed: Empty message');
      return;
    }

    if (!groupId) {
      showFeedback('error', 'Groupe non sélectionné');
      console.log('[JOIN_REQUEST] Validation failed: No group ID');
      return;
    }

    setLoading(true);
    console.log('[JOIN_REQUEST] Sending request to:', `${API}/groups/${groupId}/join-request`);
    
    try {
      const token = localStorage.getItem('token');
      const requestPayload = {
        group_id: groupId,
        message: joinRequest.message.trim()
      };
      
      console.log('[JOIN_REQUEST] Request payload:', requestPayload);
      
      const response = await fetch(`${API}/groups/${groupId}/join-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(requestPayload)
      });

      console.log('[JOIN_REQUEST] Response status:', response.status);
      const data = await response.json();
      console.log('[JOIN_REQUEST] Response data:', data);
      
      if (response.ok) {
        showFeedback('success', 'Demande d\'adhésion envoyée avec succès !');
        console.log('[JOIN_REQUEST] ✅ Request sent successfully');
        setSelectedGroup(null);
        setJoinRequest({ message: '' });
        // Refresh data
        await fetchMyRequests();
        await fetchAvailableGroups();
      } else {
        const errorMessage = data.detail || data.message || 'Erreur lors de l\'envoi de la demande';
        showFeedback('error', errorMessage);
        console.error('[JOIN_REQUEST] ❌ Server error:', errorMessage);
      }
    } catch (error) {
      console.error('[JOIN_REQUEST] ❌ Network error:', error);
      showFeedback('error', 'Erreur de connexion. Vérifiez votre connexion internet.');
    } finally {
      setLoading(false);
      console.log('[JOIN_REQUEST] Request completed');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'En attente' },
      accepted: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Acceptée' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Refusée' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <IconComponent className="w-3 h-3" />
        {config.text}
      </Badge>
    );
  };

  const filteredGroups = availableGroups.filter(group =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-20">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Retour"
          >
            <ArrowLeft size={20} />
            <span>Retour</span>
          </button>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <UserPlus className="w-6 h-6 text-blue-600" />
          Adhésions aux Groupes
        </h1>
        <p className="text-gray-600 mt-1">
          Rejoignez des groupes d'épargne ou suivez vos demandes
        </p>
      </div>

      {/* Feedback */}
      {feedback.visible && (
        <div className={`mb-4 p-4 rounded-lg ${
          feedback.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 
          'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {typeof feedback.message === 'string' ? feedback.message : JSON.stringify(feedback.message)}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('my-requests')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'my-requests'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Mes Demandes
          </button>
          <button
            onClick={() => setActiveTab('available-groups')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'available-groups'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Groupes Disponibles
          </button>
        </div>
      </div>

      {/* My Requests Tab */}
      {activeTab === 'my-requests' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Mes Demandes d'Adhésion ({myRequests.length})
          </h2>
          
          {myRequests.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <UserPlus className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Aucune demande d'adhésion envoyée</p>
                <Button 
                  onClick={() => setActiveTab('available-groups')}
                  className="mt-4"
                >
                  Explorer les groupes
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {myRequests.map((request) => (
                <Card key={request.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{request.group_name}</h3>
                        <p className="text-sm text-gray-600">{request.group_description}</p>
                      </div>
                      {getStatusBadge(request.status)}
                    </div>
                    
                    <div className="text-sm text-gray-600 mb-2">
                      <p><strong>Message :</strong> {request.message}</p>
                      <p><strong>Envoyé le :</strong> {new Date(request.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>

                    {request.admin_response && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700">
                          <strong>Réponse de l'admin :</strong> {request.admin_response}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Available Groups Tab */}
      {activeTab === 'available-groups' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Rechercher des groupes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <h2 className="text-lg font-semibold text-gray-900">
            Groupes Disponibles ({filteredGroups.length})
          </h2>

          {filteredGroups.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  {searchTerm ? 'Aucun groupe trouvé' : 'Aucun groupe disponible'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredGroups.map((group) => (
                <Card key={group.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold">
                              {group.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{group.name}</h3>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {group.member_count || group.members?.length || 0} membres
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Créé le {new Date(group.created_at).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-3">
                          {group.description || 'Aucune description disponible'}
                        </p>

                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="outline">
                            {group.currency || 'KES'} {group.contribution_amount || 0}
                          </Badge>
                          <Badge variant="outline" className={
                            group.status === 'active' ? 'text-green-600' : 'text-gray-600'
                          }>
                            {group.status === 'active' ? 'Actif' : group.status}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedGroup(group)}
                        className="flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" />
                        Voir détails
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setSelectedGroup(group)}
                        className="flex items-center gap-1"
                      >
                        <Send className="w-3 h-3" />
                        Demander l'adhésion
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Rewarded Ad Section - Premium Features */}
      <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-100">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Débloquez Plus de Fonctionnalités</h3>
          <p className="text-gray-600 mb-6">
            Regardez une courte publicité pour débloquer l'accès aux fonctionnalités premium et améliorer votre expérience d'adhésion.
          </p>
          
          <RewardedAdTrigger 
            rewardType="premium-features"
            label="Débloquer Premium (30s)"
          />
        </div>
      </div>

      {/* Join Request Modal */}
      {selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Demande d'adhésion
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-900">{selectedGroup.name}</h3>
                <p className="text-sm text-blue-700">{selectedGroup.description}</p>
              </div>

              <div>
                <Label htmlFor="message">Message de motivation *</Label>
                <Textarea
                  id="message"
                  placeholder="Expliquez pourquoi vous souhaitez rejoindre ce groupe..."
                  value={joinRequest.message}
                  onChange={(e) => setJoinRequest({ message: e.target.value })}
                  rows={4}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    console.log('[JOIN_REQUEST] Button clicked for group:', selectedGroup?.id);
                    handleJoinRequest(selectedGroup.id);
                  }}
                  disabled={loading || !joinRequest.message.trim() || !selectedGroup}
                  className={`flex-1 transition-all duration-200 ${
                    loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Envoi...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer la demande
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    console.log('[JOIN_REQUEST] Cancel clicked');
                    setSelectedGroup(null);
                    setJoinRequest({ message: '' });
                  }}
                  disabled={loading}
                  className="transition-all duration-200 hover:scale-105"
                >
                  Annuler
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default JoinRequestsPage;